import { useEffect, useState } from 'react';
import { useSettings } from '../store/settings';

/** The narrowest a column may get before the board shows one at a time. */
export const COLUMN_MIN = 220;
/** The wide board's gap between columns and its side padding together, as in Board.module.css. */
const COLUMN_GAP = 20;
const BOARD_PADDING = 48;

/** The window width from which every column gets at least COLUMN_MIN side by side. */
export function columnsFit(count: number): number {
  return count * COLUMN_MIN + (count - 1) * COLUMN_GAP + BOARD_PADDING;
}

export function useMedia(query: string): boolean {
  const [matches, setMatches] = useState(() => (typeof window === 'undefined' ? false : window.matchMedia(query).matches));
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

/**
 * One column at a time, with the bar at the bottom: on a phone, and in any window too narrow
 * for all of the user's columns. The rest of the app turns narrow at 720px, in its stylesheets.
 */
export function useSingleColumn(): boolean {
  const count = useSettings((s) => s.columnCount);
  return useMedia(`(max-width: ${columnsFit(count) - 1}px)`);
}

/** Re-renders on an interval so time-based state (due badges, the energy lens) stays fresh. */
export function useNow(intervalMs = 60_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    const onVisible = () => {
      if (document.visibilityState === 'visible') setNow(Date.now());
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [intervalMs]);
  return now;
}
